import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { toLatinDigits } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Checkbox, Input, Stack } from "@wato/ui";
import { isApiError } from "../../api/client";
import { useApi, useErrorMessage } from "../../api/context";
import type { TwoFactorEnrollment } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { AuthLayout, PageLoading } from "../../components/common";

/**
 * TOTP enrollment (`/security/two-factor/setup`), reachable with a full session or with the
 * restricted session of mandatory 2FA (ADR-0013). Backup codes are shown exactly once.
 */
export function TwoFactorSetupPage() {
  const { t } = useI18n();
  const api = useApi();
  const { state, refresh } = useAuth();
  const navigate = useNavigate();
  const errorMessage = useErrorMessage();
  const [enrollment, setEnrollment] = useState<TwoFactorEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [alreadyOn, setAlreadyOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.status === "loading") return <PageLoading />;
  if (state.status === "anonymous" || state.status === "unreachable") {
    return <Navigate to="/login" replace />;
  }
  const required = state.me.twoFactor.setupRequired;

  async function start() {
    setBusy(true);
    setError(null);
    try {
      setEnrollment(await api.enrollTwoFactor());
    } catch (caught) {
      if (isApiError(caught) && caught.code === "AUTH_2FA_ALREADY_ENABLED") setAlreadyOn(true);
      else setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.confirmTwoFactor(toLatinDigits(code).trim());
      setBackupCodes(result.backupCodes);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    await refresh();
    await navigate("/", { replace: true });
  }

  return (
    <AuthLayout title={t("dashboard.auth.setup.title")} subtitle={t("dashboard.auth.setup.intro")}>
      <Stack gap={4}>
        {required && !backupCodes && (
          <Alert tone="warning">{t("dashboard.auth.setup.required")}</Alert>
        )}
        {error && <Alert tone="error">{error}</Alert>}

        {alreadyOn ? (
          <>
            <Alert tone="success">{t("dashboard.auth.setup.alreadyEnabled")}</Alert>
            <Button onClick={() => void finish()}>{t("dashboard.auth.setup.continue")}</Button>
          </>
        ) : backupCodes ? (
          <>
            <Stack gap={1}>
              <h2 className="text-base font-semibold text-fg">
                {t("dashboard.auth.setup.backupTitle")}
              </h2>
              <p className="text-sm text-fg-muted">{t("dashboard.auth.setup.backupBody")}</p>
            </Stack>
            <ul
              aria-label={t("dashboard.auth.setup.backupListLabel")}
              dir="ltr"
              className="grid grid-cols-2 gap-2 rounded-md bg-surface-sunken p-4 font-mono text-sm text-fg"
            >
              {backupCodes.map((backup) => (
                <li key={backup}>{backup}</li>
              ))}
            </ul>
            <Checkbox
              label={t("dashboard.auth.setup.backupAck")}
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <Button disabled={!acknowledged} onClick={() => void finish()}>
              {t("dashboard.auth.setup.continue")}
            </Button>
          </>
        ) : enrollment ? (
          <form onSubmit={confirm} noValidate>
            <Stack gap={4}>
              <p className="text-sm text-fg">{t("dashboard.auth.setup.scan")}</p>
              <img
                src={enrollment.qrDataUrl}
                alt={t("dashboard.auth.setup.qrAlt")}
                className="size-48 self-center rounded-md bg-surface"
              />
              <Stack gap={1}>
                <p className="text-sm text-fg-muted">{t("dashboard.auth.setup.manualKey")}</p>
                <code dir="ltr" className="break-all rounded-sm bg-surface-sunken p-2 text-sm">
                  {enrollment.secret}
                </code>
              </Stack>
              <Input
                dir="ltr"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                required
                label={t("dashboard.auth.setup.code")}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <Button type="submit" fullWidth loading={busy}>
                {t("dashboard.auth.setup.confirm")}
              </Button>
            </Stack>
          </form>
        ) : (
          <Button onClick={() => void start()} loading={busy}>
            {t("dashboard.auth.setup.start")}
          </Button>
        )}
      </Stack>
    </AuthLayout>
  );
}
